import React, { useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { cn } from '../../ui';

/**
 * SortableList
 *
 * Lista reordenable por drag-and-drop usando la HTML5 Drag and Drop API
 * nativa — SIN librerías externas (el proyecto no introduce dnd-kit ni
 * react-beautiful-dnd).
 *
 * Se usa en el editor de tipos para:
 *   - reordenar los campos del schema
 *   - reordenar las opciones de un campo select
 *
 * Props:
 *   items: array de cualquier cosa (objetos/strings)
 *   onReorder: (newItems) => void   se llama al soltar en nueva posición
 *   getKey: (item, index) => string|number   clave estable de cada item
 *   renderItem: (item, index, dragHandleProps) => ReactNode
 *       dragHandleProps debe spreadearse en el elemento que actúa de asa.
 *   className?: string  clases del contenedor
 *   itemClassName?: string  clases de cada fila
 *
 * El asa de arrastre la decide el consumidor: renderItem recibe
 * dragHandleProps que hay que poner en el icono/elemento "agarrable".
 * Así el resto de la fila (inputs, selects) sigue siendo interactivo.
 */

export default function SortableList({
  items,
  onReorder,
  getKey,
  renderItem,
  className,
  itemClassName,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragItemRef = useRef(null);

  function handleDragStart(e, index) {
    setDragIndex(index);
    dragItemRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    // Necesario en Firefox para que arranque el drag
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch (_) {
      /* algunos navegadores lanzan si no hay datos; ignorable */
    }
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== overIndex) setOverIndex(index);
  }

  function handleDrop(e, index) {
    e.preventDefault();
    const from = dragItemRef.current;
    const to = index;
    if (from === null || from === undefined || from === to) {
      reset();
      return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
    reset();
  }

  function reset() {
    setDragIndex(null);
    setOverIndex(null);
    dragItemRef.current = null;
  }

  return (
    <div className={className}>
      {items.map((item, index) => {
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== index;

        const dragHandleProps = {
          draggable: true,
          onDragStart: (e) => handleDragStart(e, index),
          onDragEnd: reset,
          className: 'cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600',
          'aria-label': 'Arrastrar para reordenar',
          role: 'button',
          tabIndex: 0,
        };

        return (
          <div
            key={getKey(item, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            className={cn(
              'transition-colors',
              isDragging && 'opacity-40',
              isOver && 'bg-blue-50/60 outline-2 outline-dashed outline-blue-300',
              itemClassName
            )}
          >
            {renderItem(item, index, dragHandleProps)}
          </div>
        );
      })}
    </div>
  );
}

/**
 * DragHandle — icono de asa estándar (⋮⋮). Conveniencia para el
 * caso común. Recibe los dragHandleProps y los spreadea.
 */
export function DragHandle({ dragHandleProps, size = 'w-4 h-4' }) {
  return (
    <span {...dragHandleProps} className={cn(dragHandleProps.className, 'inline-flex')}>
      <GripVertical className={size} />
    </span>
  );
}
